import React from 'react';
import { useBrandingColors } from '../../hooks/useBrandingColors';

interface HierarchicalItem {
  id: string;
  label: string;
  count?: number;
  children?: HierarchicalItem[];
}

interface HierarchicalCheckboxProps {
  item: HierarchicalItem;
  checkedItems: string[];
  onToggle: (id: string, checked: boolean) => void;
  level?: number;
  expandedItems: string[];
  onToggleExpanded: (id: string) => void;
}

const HierarchicalCheckbox: React.FC<HierarchicalCheckboxProps> = ({
  item,
  checkedItems,
  onToggle,
  level = 0,
  expandedItems,
  onToggleExpanded
}) => {
  const colors = useBrandingColors();
  const hasChildren = item.children && item.children.length > 0;
  const isChecked = checkedItems.includes(item.id);
  const isExpanded = expandedItems.includes(item.id);

  // Подсчет выбранных дочерних элементов
  const getSelectedChildrenCount = (children: HierarchicalItem[]): { selected: number; total: number } => {
    let selected = 0;
    let total = children.length;
    
    children.forEach(child => {
      if (checkedItems.includes(child.id)) {
        selected++;
      }
      if (child.children) {
        const childStats = getSelectedChildrenCount(child.children);
        selected += childStats.selected;
        total += childStats.total;
      }
    });
    
    return { selected, total };
  };

  // Определение состояния чекбокса
  const getCheckboxState = () => {
    if (!hasChildren) {
      return { checked: isChecked, indeterminate: false };
    }

    const { selected, total } = getSelectedChildrenCount(item.children!);
    
    if (selected === 0) {
      if (isChecked) {
        return { checked: true, indeterminate: false };
      }
      return { checked: false, indeterminate: false };
    } else if (selected === total) {
      return { checked: true, indeterminate: false };
    } else {
      return { checked: false, indeterminate: true };
    }
  };

  const checkboxState = getCheckboxState();

  const checkboxId = `hierarchical-checkbox-${item.id}`;

  return (
    <div data-testid="modal-item">
      <div
        className="py-2 px-3 hover:bg-gray-50 flex items-center"
        data-testid="modal-item-row"
        data-id={item.id}
        data-level={level}
        data-leaf={hasChildren ? 'false' : 'true'}
        data-expanded={hasChildren ? (isExpanded ? 'true' : 'false') : undefined}
      >
        {/* Стрелка для родительских элементов */}
        {hasChildren && (
          <button
            data-testid="modal-item-expand"
            data-expanded={isExpanded ? 'true' : 'false'}
            aria-expanded={isExpanded}
            onClick={(e) => {
              e.stopPropagation();
              onToggleExpanded(item.id);
            }}
            className="w-4 h-4 mr-2 text-gray-400 hover:text-gray-600 flex items-center justify-center"
          >
            <svg
              className={`w-3 h-3 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}
        
        {/* Чекбокс */}
        <span className="inline-block w-4 h-4 mr-3">
          <input
            id={checkboxId}
            data-testid="modal-item-checkbox"
            type="checkbox"
            checked={checkboxState.checked}
            ref={(el) => {
              if (el) {
                el.indeterminate = checkboxState.indeterminate;
              }
            }}
            onChange={(e) => {
              e.stopPropagation();
              // Если элемент в состоянии indeterminate, выбираем все дочерние элементы
              const shouldCheck = checkboxState.indeterminate ? true : e.target.checked;
              onToggle(item.id, shouldCheck);
            }}
            className="h-4 w-4 border-gray-300 rounded focus:ring-2"
            style={{ 
              accentColor: colors.primary,
              color: colors.primary
            }}
          />
        </span>
        
        {/* Текст и счетчик */}
        <label
          htmlFor={checkboxId}
          data-testid="modal-item-label"
          className="text-sm text-gray-700 cursor-pointer flex items-center flex-1"
        >
          {item.label}
          {item.count !== undefined && (
            <span className="text-xs text-gray-500 ml-2">{item.count}</span>
          )}
        </label>
      </div>
      
      {hasChildren && isExpanded && (
        <div className="ml-8" data-testid="modal-item-children">
          {item.children!.map((child) => (
            <HierarchicalCheckbox
              key={child.id}
              item={child}
              checkedItems={checkedItems}
              onToggle={onToggle}
              level={level + 1}
              expandedItems={expandedItems}
              onToggleExpanded={onToggleExpanded}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HierarchicalCheckbox;
